import { useEffect, useRef, useState } from 'react';

/**
 * Custom dropdown for category / subcategory selection.
 *
 * Native <select> can't render per-option buttons, so this replaces it
 * with a button + panel we control. Custom entries show an inline × on
 * the right of their row (delete immediately); base entries don't. An
 * "+ Add new…" row sits at the bottom.
 *
 *   <ClassificationSelect
 *     value={category}
 *     options={Object.keys(taxonomy.categories)}
 *     isCustom={(name) => isCustomCat(name)}
 *     onSelect={(name) => { onCategory(name); onSubcategory(''); }}
 *     onDelete={(name) => tryDeleteCategory(name)}
 *     onAddNew={() => setAddingCategory(true)}
 *     placeholder="— none —"
 *     busy={busy}
 *   />
 */
export default function ClassificationSelect({
  value,
  options,
  isCustom,
  onSelect,
  onDelete,
  onAddNew,
  placeholder = '— none —',
  addLabel = '+ Add new…',
  busy = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="csel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`csel-trigger${value ? '' : ' is-empty'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ellipsis">{value || placeholder}</span>
        <span className="csel-caret" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div role="listbox" className="csel-panel">
          <Row
            label={placeholder}
            selected={!value}
            onSelect={() => { onSelect(''); setOpen(false); }}
          />
          {options.map((name) => (
            <Row
              key={name}
              label={name}
              selected={name === value}
              deletable={isCustom(name)}
              busy={busy}
              onSelect={() => { onSelect(name); setOpen(false); }}
              onDelete={() => onDelete(name)}
            />
          ))}
          <Row
            label={addLabel}
            add
            onSelect={() => { setOpen(false); onAddNew(); }}
          />
        </div>
      )}
    </div>
  );
}

function Row({ label, selected, deletable, add, busy, onSelect, onDelete }) {
  return (
    <div className={`cls-select-row${selected ? ' is-on' : ''}`}>
      <button
        type="button"
        role="option"
        aria-selected={!!selected}
        onClick={onSelect}
        className={`csel-option${selected ? ' is-on' : ''}${add ? ' is-add' : ''}`}
      >
        {label}
      </button>
      {deletable && (
        <button
          type="button"
          className="csel-del"
          title={`Delete "${label}"`}
          aria-label={`Delete "${label}"`}
          disabled={busy}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >
          ✕
        </button>
      )}
    </div>
  );
}
